import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../contexts/AuthContext';
import './ProfileQuestionnaire.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ProfileQuestionnaire = ({ onComplete }) => {
  const { authFetch } = useAuth();
  const fileInputRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [imageUploading, setImageUploading] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    weight: '',
    height: '',
    heightFeet: '',
    heightInches: '',
    unitSystem: 'metric', // 'metric' or 'imperial'
    preferredUnits: 'km', // 'km' or 'miles'
    temperatureUnits: 'celsius', // 'celsius' or 'fahrenheit'
    profileImage: null,
    profileImagePreview: null,

    // Step 2: Recent Race Performance (for fitness level calculation)
    recentRaceDistance: '5k',
    recentRaceTime: '',

    // Personal Bests (optional)
    personalBests: {
      '1km': '',
      '1mile': '',
      '5km': '',
      '10km': '',
      'half_marathon': '',
      'marathon': ''
    },

    // Step 3: Training Goal
    goalRace: '5k',
    planLength: '16',
    hasRaceDate: true, // Whether user has a specific race date
    goalRaceDate: '',
    planStartDate: '', // For plans without race date
    
    // Step 4: Training Level
    currentWeeklyKm: '', // Set automatically based on level selection
    mileageLevel: '', // performance, high_performance, or elite
    daysPerWeek: '', // Will be determined by mileage level selection
    longRunDay: 'sunday', // Day of the week for long run
    
    // Step 5: Additional Info
    injuries: '',
    preferences: ''
  });

  const [calculatedVDOT, setCalculatedVDOT] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mileageRanges, setMileageRanges] = useState([]);

  const totalSteps = 5;

  const raceOptions = [
    { value: '1500m', label: '1500m' },
    { value: '5k', label: '5K' },
    { value: '10k', label: '10K' },
    { value: 'half_marathon', label: 'Half Marathon' },
    { value: 'marathon', label: 'Marathon' },
    { value: 'ultra_marathon', label: 'Ultra Marathon' }
  ];

  const fetchMileageRanges = async (raceDistance) => {
    try {
      const ranges = [];
      const levelConfig = {
        'performance': { label: 'Performance', daysPerWeek: '4-5', apiLevel: 'beginner' },
        'high_performance': { label: 'High Performance', daysPerWeek: '5-6', apiLevel: 'intermediate' },
        'elite': { label: 'Elite', daysPerWeek: '6-7', apiLevel: 'advanced' }
      };

      for (const [level, config] of Object.entries(levelConfig)) {
        const response = await fetch(
          `${API_BASE_URL}/api/training-plans/recommended-mileage?goal_race=${raceDistance}&experience_level=${config.apiLevel}`
        );
        const data = await response.json();
        ranges.push({
          level: level,
          label: config.label,
          start: data.starting_weekly_km,
          peak: data.peak_weekly_km,
          daysPerWeek: config.daysPerWeek
        });
      }
      setMileageRanges(ranges);

      // If a mileage level is already selected, update the currentWeeklyKm with the new range values
      setFormData(prev => {
        if (prev.mileageLevel) {
          const selectedRange = ranges.find(r => r.level === prev.mileageLevel);
          if (selectedRange) {
            return {
              ...prev,
              currentWeeklyKm: selectedRange.start,
              daysPerWeek: selectedRange.daysPerWeek
            };
          }
        }
        return prev;
      });
    } catch (err) {
      console.error('Error fetching mileage ranges:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Fetch mileage ranges when goal race changes
    if (name === 'goalRace') {
      fetchMileageRanges(value);
    }

    setError(null);
  };

  const handlePBChange = (distance, value) => {
    setFormData(prev => ({
      ...prev,
      personalBests: {
        ...prev.personalBests,
        [distance]: value
      }
    }));

    setError(null);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }

      setError(null);
      setImageUploading(true);

      try {
        // Create FormData for file upload
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        // Upload to backend
        const response = await authFetch('/api/user/profile/upload-image', {
          method: 'POST',
          body: formDataUpload
        });

        if (response.ok) {
          const data = await response.json();
          // Construct full URL for display
          const imageUrl = data.image_url.startsWith('http')
            ? data.image_url
            : `${API_BASE_URL}${data.image_url}`;

          setFormData(prev => ({
            ...prev,
            profileImage: data.image_url,
            profileImagePreview: imageUrl
          }));
        } else {
          const errData = await response.json();
          let errorMsg = 'Failed to upload image';
          if (typeof errData.detail === 'string') {
            errorMsg = errData.detail;
          } else if (Array.isArray(errData.detail) && errData.detail.length > 0) {
            errorMsg = errData.detail[0].msg || errData.detail[0].message || errorMsg;
          }
          setError(errorMsg);
        }
      } catch (err) {
        console.error('Image upload error:', err);
        setError('Failed to upload image. Please try again.');
      } finally {
        setImageUploading(false);
      }
    }
  };

  // Validate time format (MM:SS or HH:MM:SS or H:MM:SS)
  const isValidTimeFormat = (time) => {
    if (!time || time.trim() === '') return true; // Empty is valid (optional field)

    // Match MM:SS (e.g., 22:30, 4:15) or HH:MM:SS / H:MM:SS (e.g., 1:45:00, 3:30:45)
    const timeRegex = /^(\d{1,2}):([0-5]?\d)(?::([0-5]?\d))?$/;
    const match = time.trim().match(timeRegex);

    if (!match) return false;

    const secondPart = parseInt(match[2], 10);
    const thirdPart = match[3] ? parseInt(match[3], 10) : null;

    // Validate seconds are 0-59 (minutes in HH:MM:SS also must be 0-59)
    if (thirdPart !== null) {
      return secondPart <= 59 && thirdPart <= 59;
    }
    return secondPart <= 59;
  };

  const calculateVDOT = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/vdot/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          distance: formData.recentRaceDistance,
          time: formData.recentRaceTime
        })
      });

      if (!response.ok) {
        throw new Error('Failed to calculate fitness level');
      }

      const data = await response.json();
      setCalculatedVDOT(data.vdot);
      return data.vdot;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.firstName || !formData.lastName || !formData.dateOfBirth) {
          setError('Please fill in all required fields');
          return false;
        }
        const age = calculateAge(formData.dateOfBirth);
        if (age < 13 || age > 100) {
          setError('You must be between 13 and 100 years old');
          return false;
        }
        break;
      case 2:
        if (!formData.recentRaceTime) {
          setError('Please enter your recent race time');
          return false;
        }
        // Validate time format
        const timeRegex = /^(\d{1,2}):(\d{2})$|^(\d{1,2}):(\d{2}):(\d{2})$/;
        if (!timeRegex.test(formData.recentRaceTime)) {
          setError('Time must be in MM:SS or HH:MM:SS format (e.g., 22:30 or 1:45:00)');
          return false;
        }
        break;
      case 3:
        if (formData.hasRaceDate) {
          // Validate race date
          if (!formData.goalRaceDate) {
            setError('Please select your goal race date');
            return false;
          }
          // Check date is far enough in future based on plan length
          const goalDate = new Date(formData.goalRaceDate);
          const today = new Date();
          const weeksUntilRace = Math.floor((goalDate - today) / (7 * 24 * 60 * 60 * 1000));
          const requiredWeeks = parseInt(formData.planLength);
          
          if (weeksUntilRace < requiredWeeks) {
            setError(`Your race date must be at least ${requiredWeeks} weeks away for a ${requiredWeeks}-week plan. Currently ${weeksUntilRace} weeks away.`);
            return false;
          }
        } else {
          // Validate start date
          if (!formData.planStartDate) {
            setError('Please select when you would like to start training');
            return false;
          }
        }
        break;
      case 4:
        if (!formData.mileageLevel) {
          setError('Please select a training level');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) {
      return;
    }

    // If leaving step 2, calculate fitness level
    if (currentStep === 2 && !calculatedVDOT) {
      const fitnessLevel = await calculateVDOT();
      if (!fitnessLevel) {
        return; // Error already set by calculateVDOT
      }
    }

    // If moving to step 4, fetch mileage ranges for selected race
    if (currentStep === 3 && mileageRanges.length === 0) {
      await fetchMileageRanges(formData.goalRace);
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setError(null);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let startDateStr;
      let raceDateStr;
      
      if (formData.hasRaceDate) {
        // Calculate start date by working backwards from race date
        const raceDate = new Date(formData.goalRaceDate);
        const weeksInPlan = parseInt(formData.planLength);
        const startDate = new Date(raceDate.getTime() - (weeksInPlan * 7 * 24 * 60 * 60 * 1000));
        startDateStr = startDate.toISOString().split('T')[0];
        raceDateStr = formData.goalRaceDate;
      } else {
        // Use provided start date and calculate end date
        const startDate = new Date(formData.planStartDate);
        const weeksInPlan = parseInt(formData.planLength);
        const endDate = new Date(startDate.getTime() + (weeksInPlan * 7 * 24 * 60 * 60 * 1000));
        startDateStr = formData.planStartDate;
        raceDateStr = endDate.toISOString().split('T')[0];
      }

      // Map frontend level names to backend API levels
      const levelToApiLevel = {
        'performance': 'beginner',
        'high_performance': 'intermediate',
        'elite': 'advanced'
      };

      // Generate training plan using calculated fitness level and dates
      const response = await fetch(`${API_BASE_URL}/api/training-plans/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vdot: calculatedVDOT,
          goal_race: formData.goalRace,
          goal_race_date: raceDateStr,
          current_weekly_km: parseFloat(formData.currentWeeklyKm),
          experience_level: levelToApiLevel[formData.mileageLevel],
          start_date: startDateStr,
          long_run_day: formData.longRunDay
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate training plan');
      }

      const data = await response.json();
      
      // Pass complete profile + plan to parent
      if (onComplete) {
        onComplete({
          profile: formData,
          vdot: calculatedVDOT,
          plan: data.plan,
          planLength: formData.planLength,
          startDate: startDateStr,
          hasRaceDate: formData.hasRaceDate
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getMinRaceDate = () => {
    const today = new Date();
    const weeksAhead = parseInt(formData.planLength) || 16;
    const minDate = new Date(today.getTime() + (weeksAhead * 7 * 24 * 60 * 60 * 1000));
    return minDate.toISOString().split('T')[0];
  };

  const getMinStartDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const formatRaceName = (race) => {
    const names = {
      '1500m': '1500m',
      '5k': '5K',
      '10k': '10K',
      'half_marathon': 'Half Marathon',
      'marathon': 'Marathon',
      'ultra_marathon': 'Ultra Marathon'
    };
    return names[race] || race;
  };

  // Calculate training start date
  const getTrainingStartDate = () => {
    if (formData.hasRaceDate && formData.goalRaceDate) {
      const raceDate = new Date(formData.goalRaceDate);
      const weeksInPlan = parseInt(formData.planLength);
      const startDate = new Date(raceDate.getTime() - (weeksInPlan * 7 * 24 * 60 * 60 * 1000));
      return startDate.toLocaleDateString();
    } else if (formData.planStartDate) {
      return new Date(formData.planStartDate).toLocaleDateString();
    }
    return null;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <h2>Welcome! Let's create your profile</h2>
            <p className="step-description">First, tell us a bit about yourself</p>

            {/* Profile Photo Upload */}
            <div className="profile-image-section">
              <div
                className={`profile-image-preview ${imageUploading ? 'uploading' : ''}`}
                onClick={() => !imageUploading && fileInputRef.current?.click()}
              >
                {imageUploading ? (
                  <div className="profile-image-placeholder">
                    <span className="upload-spinner">⏳</span>
                    <span className="placeholder-text">Uploading...</span>
                  </div>
                ) : formData.profileImagePreview ? (
                  <img src={formData.profileImagePreview} alt="Profile" />
                ) : (
                  <div className="profile-image-placeholder">
                    <span>📷</span>
                    <span className="placeholder-text">Add Photo</span>
                  </div>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="change-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={imageUploading}
              >
                {imageUploading ? 'Uploading...' : (formData.profileImagePreview ? 'Change Photo' : 'Add Photo')}
              </button>
              <small className="photo-hint">Optional - you can add this later</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="First name"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="dateOfBirth">Date of Birth *</label>
                <DatePicker
                  id="dateOfBirth"
                  selected={formData.dateOfBirth ? new Date(formData.dateOfBirth + 'T00:00:00') : null}
                  onChange={(date) => {
                    if (!date) {
                      setFormData(prev => ({ ...prev, dateOfBirth: '' }));
                      return;
                    }
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    const dateStr = `${year}-${month}-${day}`;
                    setFormData(prev => ({ ...prev, dateOfBirth: dateStr }));
                  }}
                  dateFormat="dd/MM/yyyy"
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  openToDate={new Date(new Date().setFullYear(new Date().getFullYear() - 30))}
                  maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 13))}
                  minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 100))}
                  placeholderText="Select date"
                  className="datepicker-input"
                  showIcon
                />
              </div>

              <div className="form-group">
                <label htmlFor="gender">Gender *</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>

            {/* Unit System Selection */}
            <div className="form-group">
              <label htmlFor="unitSystem">Unit Preferences *</label>
              <select
                id="unitSystem"
                name="unitSystem"
                value={formData.unitSystem}
                onChange={handleInputChange}
              >
                <option value="metric">Metric (kg, cm)</option>
                <option value="imperial">Imperial (lbs, ft/in)</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="weight">Weight {formData.unitSystem === 'metric' ? '(kg)' : '(lbs)'} *</label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder={formData.unitSystem === 'metric' ? '70' : '154'}
                  min="20"
                  max={formData.unitSystem === 'metric' ? '300' : '660'}
                  step="0.1"
                />
              </div>

              {formData.unitSystem === 'metric' ? (
                <div className="form-group">
                  <label htmlFor="height">Height (cm) *</label>
                  <input
                    type="number"
                    id="height"
                    name="height"
                    value={formData.height}
                    onChange={handleInputChange}
                    placeholder="175"
                    min="100"
                    max="250"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Height *</label>
                  <div className="height-imperial">
                    <input
                      type="number"
                      id="heightFeet"
                      name="heightFeet"
                      value={formData.heightFeet}
                      onChange={handleInputChange}
                      placeholder="5"
                      min="3"
                      max="8"
                    />
                    <span className="height-unit">ft</span>
                    <input
                      type="number"
                      id="heightInches"
                      name="heightInches"
                      value={formData.heightInches}
                      onChange={handleInputChange}
                      placeholder="9"
                      min="0"
                      max="11"
                    />
                    <span className="height-unit">in</span>
                  </div>
                </div>
              )}
            </div>

            {/* Distance Units Preference */}
            <div className="form-group">
              <label htmlFor="preferredUnits">Preferred Distance Units *</label>
              <select
                id="preferredUnits"
                name="preferredUnits"
                value={formData.preferredUnits}
                onChange={handleInputChange}
              >
                <option value="km">Kilometers (km)</option>
                <option value="miles">Miles</option>
              </select>
              <small>Used for displaying distances in your training plan</small>
            </div>

            {/* Temperature Units Preference */}
            <div className="form-group">
              <label htmlFor="temperatureUnits">Preferred Temperature Units *</label>
              <select
                id="temperatureUnits"
                name="temperatureUnits"
                value={formData.temperatureUnits}
                onChange={handleInputChange}
              >
                <option value="celsius">Celsius (°C)</option>
                <option value="fahrenheit">Fahrenheit (°F)</option>
              </select>
              <small>Used for weather-related training recommendations</small>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <h2>Recent Race Performance</h2>
            <p className="step-description">
              Help us understand your current fitness level by sharing a recent race result
            </p>

            <div className="info-box">
              <strong>💡 Why we ask:</strong> Your training paces are personalised to you. A recent race time helps set paces that feel "comfortably hard" rather than too easy or too intense. No race? A short time trial or estimate works just fine to get started.
            </div>

            <div className="form-group">
              <label htmlFor="recentRaceDistance">Race Distance *</label>
              <select
                id="recentRaceDistance"
                name="recentRaceDistance"
                value={formData.recentRaceDistance}
                onChange={handleInputChange}
              >
                {raceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="recentRaceTime">Race Time *</label>
              <input
                type="text"
                id="recentRaceTime"
                name="recentRaceTime"
                value={formData.recentRaceTime}
                onChange={handleInputChange}
                placeholder="e.g., 22:30 or 1:45:00"
              />
              <small>Format: MM:SS (e.g., 22:30) or HH:MM:SS (e.g., 1:45:00)</small>
            </div>

            {calculatedVDOT && (
              <div className="success-box">
                ✅ Your fitness level has been calculated!
              </div>
            )}

            {/* Personal Bests Section */}
            <div className="pb-section">
              <h3>Personal Bests</h3>
              <p className="pb-description">Optional: Add your PBs to track your progress over time</p>

              <div className="pb-grid">
                {[
                  { key: '1km', label: '1km' },
                  { key: '1mile', label: '1 Mile' },
                  { key: '5km', label: '5km' },
                  { key: '10km', label: '10km' },
                  { key: 'half_marathon', label: 'Half Marathon' },
                  { key: 'marathon', label: 'Marathon' }
                ].map(({ key, label }) => {
                  const value = formData.personalBests[key];
                  const isValid = isValidTimeFormat(value);
                  const showValidation = value && value.trim() !== '';
                  return (
                    <div key={key} className="pb-input-group">
                      <label htmlFor={`pb-${key}`}>{label}</label>
                      <input
                        type="text"
                        id={`pb-${key}`}
                        value={value}
                        onChange={(e) => handlePBChange(key, e.target.value)}
                        placeholder="MM:SS"
                        className={showValidation ? (isValid ? 'valid' : 'invalid') : ''}
                      />
                      {showValidation && !isValid && (
                        <span className="pb-error">Invalid format</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <small className="pb-hint">Format: MM:SS (e.g., 22:30) or HH:MM:SS (e.g., 3:45:00)</small>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <h2>Your Training Goal</h2>
            <p className="step-description">What are you training for?</p>

            <div className="info-box">
              <strong>📅 Structured Training Plans</strong>
              <p>All plans are structured over 16, 20, or 24 weeks, giving your body time to adapt, recover, and progress—no matter your starting point.</p>
            </div>

            <div className="form-group">
              <label htmlFor="goalRace">Goal Race Distance *</label>
              <select
                id="goalRace"
                name="goalRace"
                value={formData.goalRace}
                onChange={handleInputChange}
              >
                {raceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="planLength">Training Plan Length *</label>
              <select
                id="planLength"
                name="planLength"
                value={formData.planLength}
                onChange={handleInputChange}
              >
                <option value="16">16 weeks</option>
                <option value="20">20 weeks</option>
                <option value="24">24 weeks</option>
              </select>
              <small>Choose the plan duration that fits your timeline</small>
            </div>

            {/* Race Date Toggle */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="hasRaceDate"
                  checked={formData.hasRaceDate}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    hasRaceDate: e.target.checked,
                    goalRaceDate: '',
                    planStartDate: ''
                  }))}
                />
                <span>I have a specific race date</span>
              </label>
            </div>

            {/* Show race date OR start date based on toggle */}
            {formData.hasRaceDate ? (
              <div className="form-group">
                <label htmlFor="goalRaceDate">Goal Race Date *</label>
                <input
                  type="date"
                  id="goalRaceDate"
                  name="goalRaceDate"
                  value={formData.goalRaceDate}
                  onChange={handleInputChange}
                  min={getMinRaceDate()}
                />
                <small>
                  {formData.planLength && `Must be at least ${formData.planLength} weeks from today (plan will start automatically)`}
                </small>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="planStartDate">Plan Start Date *</label>
                <input
                  type="date"
                  id="planStartDate"
                  name="planStartDate"
                  value={formData.planStartDate}
                  onChange={handleInputChange}
                  min={getMinStartDate()}
                />
                <small>When would you like to start training?</small>
              </div>
            )}
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <h2>Training Level</h2>
            <p className="step-description">Select your training intensity level</p>

            {/* Training Level Selection */}
            {mileageRanges.length > 0 && (
              <div className="form-group">
                <label htmlFor="mileageLevel">Select Your Training Plan Intensity *</label>
                <div className="mileage-options">
                  {mileageRanges.map(range => (
                    <label key={range.level} className={`mileage-option ${formData.mileageLevel === range.level ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="mileageLevel"
                        value={range.level}
                        checked={formData.mileageLevel === range.level}
                        onChange={() => {
                          setFormData(prev => ({
                            ...prev,
                            mileageLevel: range.level,
                            daysPerWeek: range.daysPerWeek,
                            currentWeeklyKm: range.start // Set starting mileage based on level
                          }));
                          setError(null);
                        }}
                      />
                      <div className="mileage-option-content">
                        <strong>{range.label}</strong>
                        <p>Start: {range.start} km/week → Peak: {range.peak} km/week</p>
                        <p className="days-per-week">{range.daysPerWeek} days per week</p>
                      </div>
                    </label>
                  ))}
                </div>
                <small>Choose the intensity that matches your goals and available time</small>
              </div>
            )}

            {/* Long Run Day Selection */}
            <div className="form-group">
              <label htmlFor="longRunDay">Preferred Long Run Day *</label>
              <select
                id="longRunDay"
                name="longRunDay"
                value={formData.longRunDay}
                onChange={handleInputChange}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
              <small>Which day works best for your longest run of the week?</small>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="step-content">
            <h2>Review Your Profile</h2>
            <p className="step-description">Check your information before we create your plan</p>

            <div className="review-section">
              <div className="review-item">
                <strong>Personal Info</strong>
                <p>{formData.firstName} {formData.lastName}, {calculateAge(formData.dateOfBirth)} years old{formData.gender && `, ${formData.gender.charAt(0).toUpperCase() + formData.gender.slice(1)}`}</p>
                {formData.weight && (
                  <p>Weight: {formData.weight} {formData.unitSystem === 'metric' ? 'kg' : 'lbs'}</p>
                )}
                {(formData.height || formData.heightFeet) && (
                  <p>Height: {formData.unitSystem === 'metric'
                    ? `${formData.height} cm`
                    : `${formData.heightFeet}'${formData.heightInches || 0}"`}</p>
                )}
                <p>Distance units: {formData.preferredUnits === 'km' ? 'Kilometers' : 'Miles'}</p>
              </div>

              <div className="review-item">
                <strong>Recent Performance</strong>
                <p>{formatRaceName(formData.recentRaceDistance)} in {formData.recentRaceTime}</p>
              </div>

              <div className="review-item">
                <strong>Training Goal</strong>
                <p>{formatRaceName(formData.goalRace)}</p>
                <p>{formData.planLength}-week training plan</p>
                {formData.hasRaceDate ? (
                  <p>Race day: {new Date(formData.goalRaceDate).toLocaleDateString()}</p>
                ) : (
                  <p>Starts: {new Date(formData.planStartDate).toLocaleDateString()}</p>
                )}
              </div>

              <div className="review-item">
                <strong>Training Level</strong>
                <p>Level: {formData.mileageLevel === 'high_performance' ? 'High Performance' : formData.mileageLevel.charAt(0).toUpperCase() + formData.mileageLevel.slice(1)}</p>
                <p>Starting at {formData.currentWeeklyKm} km per week</p>
                <p>{formData.daysPerWeek} training days per week</p>
                <p>Long run: {formData.longRunDay.charAt(0).toUpperCase() + formData.longRunDay.slice(1)}</p>
                {getTrainingStartDate() && (
                  <p>First training run: {getTrainingStartDate()}</p>
                )}
              </div>

              {/* Personal Bests - only show if any PBs are entered */}
              {Object.values(formData.personalBests).some(pb => pb) && (
                <div className="review-item">
                  <strong>Personal Bests</strong>
                  <div className="review-pbs">
                    {[
                      { key: '1km', label: '1km' },
                      { key: '1mile', label: '1 Mile' },
                      { key: '5km', label: '5km' },
                      { key: '10km', label: '10km' },
                      { key: 'half_marathon', label: 'Half Marathon' },
                      { key: 'marathon', label: 'Marathon' }
                    ].filter(({ key }) => formData.personalBests[key])
                      .map(({ key, label }) => (
                        <p key={key}>{label}: {formData.personalBests[key]}</p>
                      ))}
                  </div>
                </div>
              )}
            </div>

          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="profile-questionnaire">
      <div className="questionnaire-container">
        <div className="progress-bar">
          <div className="progress-steps">
            {[1, 2, 3, 4, 5].map(step => (
              <div
                key={step}
                className={`progress-step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
              >
                <div className="step-number">{step}</div>
                <div className="step-label">
                  {step === 1 && 'About You'}
                  {step === 2 && 'Fitness Level'}
                  {step === 3 && 'Goal'}
                  {step === 4 && 'Training Status'}
                  {step === 5 && 'Review'}
                </div>
              </div>
            ))}
          </div>
          <div className="progress-fill" style={{ width: `${(currentStep / totalSteps) * 100}%` }}></div>
        </div>

        <div className="step-container">
          {renderStep()}

          {error && (
            <div className="error-message">
              <strong>⚠️ {error}</strong>
            </div>
          )}

          <div className="button-group">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="btn-secondary"
                disabled={loading}
              >
                ← Back
              </button>
            )}

            {currentStep < totalSteps ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Calculating...' : 'Next →'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                className="btn-primary btn-submit"
                disabled={loading}
              >
                {loading ? 'Creating Your Plan...' : 'Create Training Plan'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileQuestionnaire;
